import React, { memo, useEffect } from 'react';
import { NoResults, algoliaCache, ProductHits } from '~/features/productsList/components';
import { AlgoliaClientSearch } from '~/features/productsList/components';
import { ModuleContainer } from '../ModuleContainer';
import { DynamicFilters } from '~/features/productsList/components/Filters';
import {
    M140ProductListModule,
    M170SearchPage,
    MarketingSpot,
    P40ProductListPage,
    P41ProductListPage,
} from '~/lib/data-contract';
import { useFrame, useTranslation } from '~/shared/utils';

import {
    StyledCategoriesContainer,
    StyledProductList,
    StyledProductListContainer,
    StyledProductsHeader,
    StyledHeadlineWrapper,
} from './styled';

import { usePage } from '~/templates/pages';
import { N50Breadcrumb, N40SideMenu } from '~/features/navigation';
import { useAlgoliaAnalytics } from '~/shared/hooks';
import { MobileCategories } from './components/MobileCategories';
import { useSideMenu } from '~/shared/utils/sidemenu';
import { Spacer } from '~/theme';
import { Headline } from '../Headline/Headline';
import { useRouter } from 'next/router';
import { useAlgoliaInitialState } from '~/shared/utils/algolia';
import { usePLPGridLayout } from '~/features/productsList/hooks/usePLPGridLayout';
import { useModule } from '~/templates';
import { useAlgoliaConfig } from '$shared/hooks/useAlgoliaConfig';
import { useFilterUIState } from '$features/productsList/hooks';
import { ResultsCount, StyledResponsiveCount } from '~/features/productsList';
import { useMount } from 'react-use';

export type M140ProductListProps = M140ProductListModule & { marketingContent?: MarketingSpot[] };

export const M140ProductsList = memo(
    ({ itemsPerPage, category, context, sortIndex, marketingContent }: M140ProductListProps) => {
        const { asPath, locale, replace: routerReplace, query: routerQuery } = useRouter();
        const { orderIndexes } = useAlgoliaConfig();
        const { index = 0 } = useModule();
        const { data: frameData } = useFrame();
        const { initialState } = useAlgoliaInitialState(asPath.replace(new RegExp(`^/${locale}`), ''));
        const { trackSelectedCategory } = useAlgoliaAnalytics();
        const { setPreSelectedIndexKey, preSelectedIndexKey } = useFilterUIState();

        const { translate } = useTranslation();
        const page = usePage<P40ProductListPage | P41ProductListPage | M170SearchPage>();
        const gridLayout = usePLPGridLayout(page.type);
        const { data: sidemenu } = useSideMenu();

        const breadcrumb = page?.breadcrumb || {};
        const breadcrumbItems = breadcrumb?.items || [];
        const currentPageIndex = (breadcrumbItems?.length || 0) - 1;
        const currentPage = breadcrumbItems?.length ? breadcrumbItems[currentPageIndex] : null;

        const hitsPerPage = itemsPerPage ?? 24;

        useEffect(() => {
            if (preSelectedIndexKey) {
                setPreSelectedIndexKey(null);
            }
            if (sortIndex && !routerQuery.sortBy) {
                const activeSortingKey = orderIndexes.find((i) => i.key.includes(sortIndex))?.key;

                if (activeSortingKey) {
                    setPreSelectedIndexKey(activeSortingKey);

                    routerReplace(
                        {
                            pathname: asPath,
                            query: {
                                sortBy: activeSortingKey,
                            },
                        },
                        undefined,
                        { shallow: true }
                    ).finally();
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [sortIndex]);

        useMount(() => {
            if (category) {
                trackSelectedCategory(`categoryKeys:${category}`);
            }
        });

        return (
            <ModuleContainer spacingTop="none" hasGutter>
                <AlgoliaClientSearch
                    hitsPerPage={hitsPerPage}
                    algoliaApp={frameData?.search?.algolia?.appKey || ''}
                    algoliaToken={frameData?.search?.algolia?.apiToken || ''}
                    category={category}
                    context={context}
                    initialState={initialState}
                >
                    <StyledProductsHeader topMargin={index === 0 ? 'default' : 'little'}>
                        <N50Breadcrumb>
                            <StyledResponsiveCount variant="mobile">
                                <ResultsCount spacing="header" />
                            </StyledResponsiveCount>
                        </N50Breadcrumb>
                        <Spacer space="2" />
                        <StyledHeadlineWrapper md>
                            <Headline variant="display1">
                                {currentPage
                                    ? currentPage.title
                                    : translate('commerce.allProducts')}
                            </Headline>
                        </StyledHeadlineWrapper>
                    </StyledProductsHeader>
                    <MobileCategories menus={sidemenu} breadcrumbs={breadcrumbItems} />
                    <StyledProductList>
                        {gridLayout === 'with-menu' && (
                            <StyledCategoriesContainer>
                                <N40SideMenu menus={sidemenu} breadcrumbs={breadcrumbItems} />
                            </StyledCategoriesContainer>
                        )}
                        <StyledProductListContainer>
                            <DynamicFilters facets={['*']} maxValuesPerFacet={100} />
                            <ProductHits
                                cache={initialState ? null : algoliaCache}
                                hitsPerPage={hitsPerPage}
                                marketingContent={marketingContent}
                                isProductListPage
                            />

                            <NoResults type="plp" />
                        </StyledProductListContainer>
                    </StyledProductList>
                </AlgoliaClientSearch>
            </ModuleContainer>
        );
    }
);
